import { logger } from '@core/logging';
import { generateCorrelationId } from '@core/utils/correlationUtils';
import { LogCategory } from '@soteria/logger/browser';

/**
 * Options for configuring the HTTP client
 */
export interface HttpClientOptions {
  /** Base URL for all requests */
  baseUrl: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Default headers to include with all requests */
  headers?: Record<string, string>;
  /** Whether to include credentials (cookies) in requests */
  credentials?: RequestCredentials;
}

/**
 * HTTP error response details
 */
export interface HttpErrorDetails {
  status: number;
  statusText: string;
  url: string;
  correlationId: string;
  responseBody?: unknown;
}

/**
 * HTTP response error with additional context
 */
export class HttpError extends Error {
  details: HttpErrorDetails;

  constructor(message: string, details: HttpErrorDetails) {
    super(message);
    this.name = 'HttpError';
    this.details = details;
  }
}

/**
 * Request context for logging and error handling
 */
interface RequestContext {
  url: string;
  method: string;
  correlationId: string;
}

/**
 * Generic HTTP client for making REST API calls
 */
export class HttpClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private timeout: number;
  private credentials: RequestCredentials;

  /**
   * Create a new HTTP client
   * @param options Configuration options
   */
  constructor(options: HttpClientOptions) {
    this.baseUrl = options.baseUrl;
    this.timeout = options.timeout || 30000; // 30 seconds default
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    this.credentials = options.credentials || 'same-origin'; // Use same-origin as default (matches fetch default)
  }

  /**
   * Make a GET request
   * @param path The path to request (will be appended to baseUrl)
   * @param options Additional fetch options
   * @returns The response data
   */
  async get<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      method: 'GET',
      ...options,
    });
  }

  /**
   * Make a POST request
   * @param path The path to request (will be appended to baseUrl)
   * @param data The data to send in the request body
   * @param options Additional fetch options
   * @returns The response data
   */
  async post<T>(path: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: this.prepareRequestBody(data),
      ...options,
    });
  }

  /**
   * Make a PUT request
   * @param path The path to request (will be appended to baseUrl)
   * @param data The data to send in the request body
   * @param options Additional fetch options
   * @returns The response data
   */
  async put<T>(path: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: this.prepareRequestBody(data),
      ...options,
    });
  }

  /**
   * Make a PATCH request
   * @param path The path to request (will be appended to baseUrl)
   * @param data The data to send in the request body
   * @param options Additional fetch options
   * @returns The response data
   */
  async patch<T>(path: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: this.prepareRequestBody(data),
      ...options,
    });
  }

  /**
   * Make a DELETE request
   * @param path The path to request (will be appended to baseUrl)
   * @param options Additional fetch options
   * @returns The response data
   */
  async delete<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      method: 'DELETE',
      ...options,
    });
  }

  /**
   * Prepare request body based on data type
   */
  private prepareRequestBody(data?: unknown): BodyInit | undefined {
    if (data === null || data === undefined) {
      return undefined;
    }

    // If data is already a valid body type, return as is
    if (
      data instanceof FormData ||
      data instanceof File ||
      data instanceof Blob ||
      data instanceof URLSearchParams ||
      data instanceof ReadableStream ||
      typeof data === 'string'
    ) {
      return data as BodyInit;
    }

    // Otherwise, stringify as JSON
    return JSON.stringify(data);
  }

  /**
   * Make a request with the specified options
   */
  /**
   * Create request context
   */
  private createRequestContext(path: string, method?: string): RequestContext {
    const url = new URL(path, this.baseUrl).toString();
    const correlationId = generateCorrelationId();

    return {
      url,
      method: method || 'GET',
      correlationId,
    };
  }

  /**
   * Handle response
   */
  private async handleResponse<T>(response: Response, context: RequestContext): Promise<T> {
    if (!response.ok) {
      throw await this.createHttpError(response, context);
    }

    return this.parseResponse<T>(response);
  }

  /**
   * Handle request error
   */
  private handleRequestError(error: unknown, context: RequestContext): never {
    if (error instanceof HttpError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      this.logTimeout(context);
      throw new Error(`Request timed out after ${this.timeout}ms: ${context.url}`);
    }

    this.logError(error, context);
    throw error;
  }

  /**
   * Make a request with the specified options
   */
  private async request<T>(path: string, options: RequestInit): Promise<T> {
    // Create request context
    const context = this.createRequestContext(path, options.method);

    // Setup request
    const headers = this.prepareHeaders(options.headers, context.correlationId);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Log request
    this.logRequest(context);

    try {
      // Execute request
      const response = await fetch(context.url, {
        ...options,
        headers,
        signal: options.signal || controller.signal,
        credentials: options.credentials || this.credentials,
      });

      clearTimeout(timeoutId);

      return this.handleResponse<T>(response, context);
    } catch (error) {
      clearTimeout(timeoutId);
      this.handleRequestError(error, context);
    }
  }

  /**
   * Prepare request headers
   */
  private prepareHeaders(requestHeaders?: HeadersInit, correlationId?: string): Headers {
    const headers = new Headers(this.defaultHeaders);

    // Add request-specific headers if provided
    if (requestHeaders) {
      if (requestHeaders instanceof Headers) {
        requestHeaders.forEach((value, key) => {
          headers.set(key, value);
        });
      } else if (Array.isArray(requestHeaders)) {
        requestHeaders.forEach(([key, value]) => {
          if (value !== undefined) {
            headers.set(key, value);
          }
        });
      } else {
        Object.entries(requestHeaders).forEach(([key, value]) => {
          if (value !== undefined) {
            headers.set(key, value);
          }
        });
      }
    }

    // Add correlation ID header if provided
    if (correlationId) {
      headers.set('X-Correlation-ID', correlationId);
    }

    return headers;
  }

  /**
   * Log request
   */
  private logRequest(context: RequestContext): void {
    logger.debug('HTTP request initiated', {
      category: LogCategory.API,
      url: context.url,
      method: context.method,
      correlationId: context.correlationId,
    });
  }

  /**
   * Log timeout error
   */
  private logTimeout(context: RequestContext): void {
    logger.error('HTTP request timed out', {
      category: LogCategory.API,
      url: context.url,
      method: context.method,
      timeout: this.timeout,
      correlationId: context.correlationId,
    });
  }

  /**
   * Log general error
   */
  private logError(error: unknown, context: RequestContext): void {
    logger.error('HTTP request failed with exception', {
      category: LogCategory.API,
      url: context.url,
      method: context.method,
      correlationId: context.correlationId,
      error,
    });
  }

  /**
   * Create HTTP error from response
   */
  private async createHttpError(response: Response, context: RequestContext): Promise<HttpError> {
    // Parse error response body
    const responseBody = await this.parseErrorBody(response);

    // Log error
    logger.error('HTTP request failed', {
      category: LogCategory.API,
      url: context.url,
      method: context.method,
      status: response.status,
      statusText: response.statusText,
      correlationId: context.correlationId,
      responseBody,
    });

    // Create error details
    const errorDetails: HttpErrorDetails = {
      status: response.status,
      statusText: response.statusText,
      url: context.url,
      correlationId: context.correlationId,
      responseBody,
    };

    return new HttpError(`HTTP error ${response.status}: ${response.statusText}`, errorDetails);
  }

  /**
   * Parse error response body
   */
  private async parseErrorBody(response: Response): Promise<unknown> {
    try {
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        return await response.json();
      }

      return await response.text();
    } catch {
      return undefined;
    }
  }

  /**
   * Parse response based on content type
   */
  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      return await response.json();
    }

    if (contentType?.includes('text/')) {
      return (await response.text()) as unknown as T;
    }

    return (await response.blob()) as unknown as T;
  }
}
